import React, { useEffect, useState, useMemo } from "react";
import "../styles/Shops.css";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

import {
  FiPhoneCall,
  FiHeart,
  FiInstagram,
  FiFacebook,
  FiTwitter,
  FiAtSign,
  FiYoutube,
} from "react-icons/fi";

import {
  collection,
  getDocs,
  doc,
  updateDoc,
  increment,
  arrayUnion,
} from "firebase/firestore";

import { db } from "../firebase";

export default function Shops() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [ownerShops, setOwnerShops] = useState([]);
  const [volunteerShops, setVolunteerShops] = useState([]);

  const [activeTab, setActiveTab] = useState("all");
  const [expandedShop, setExpandedShop] = useState(null);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  /* --------------------------------
     Open social inside app
  -------------------------------- */
  const openSocial = (url) => {
    if (!url) return;

    window.location.href = url;
  };

  /* --------------------------------
     Get Shop Type
     
     Supports:
     - Type (Premium/Normal)
     - Type
     - shopType
  -------------------------------- */
  const getShopType = (shop) => {
    return (
      shop["Type (Premium/Normal)"] ||
      shop.Type ||
      shop.shopType ||
      "Normal"
    );
  };

  /* --------------------------------
     Check Premium
  -------------------------------- */
  const isPremiumShop = (shop) => {
    const type = getShopType(shop);

    return String(type)
      .trim()
      .toLowerCase()
      .includes("premium");
  };

  /* --------------------------------
     Get Mobile Number

     Supports:
     - Mobile Number
     - mobile
     - Mobile
  -------------------------------- */
  const getMobileNumber = (shop) => {
    return (
      shop["Mobile Number"] ||
      shop.mobile ||
      shop.Mobile ||
      ""
    );
  };

  /* --------------------------------
     Load Shops
  -------------------------------- */
  useEffect(() => {
    loadShops();
  }, []);

  const loadShops = async () => {
    try {
      setLoading(true);

      /* -----------------------
         Owner Shops
      ----------------------- */
      const ownerSnap = await getDocs(
        collection(db, "shops")
      );

      const owners = ownerSnap.docs.map((shopDoc) => ({
        rowId: shopDoc.id,
        source: "owner",
        ...shopDoc.data(),
      }));

      /* -----------------------
         Volunteer Shops
      ----------------------- */
      const volunteerSnap = await getDocs(
        collection(db, "businessSubmissions")
      );

      const volunteers = volunteerSnap.docs.map((shopDoc) => ({
        rowId: shopDoc.id,
        source: "volunteer",
        ...shopDoc.data(),
      }));

      /*
        Keep the existing newest-first behaviour.
        Do not mutate the original arrays.
      */
      setOwnerShops([...owners].reverse());
      setVolunteerShops([...volunteers].reverse());

    } catch (err) {
      console.error(
        "Failed to load shops:",
        err
      );
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------
     Current List

     IMPORTANT:
     Memoized so changing expandedShop
     does NOT recreate the array.
  -------------------------------- */
  const currentList = useMemo(() => {
    if (activeTab === "all") {
      return [
        ...ownerShops,
        ...volunteerShops,
      ];
    }

    if (activeTab === "owner") {
      return ownerShops;
    }

    return volunteerShops;
  }, [
    activeTab,
    ownerShops,
    volunteerShops,
  ]);

  /* --------------------------------
     Categories
  -------------------------------- */
  const categories = useMemo(() => {
    return [
      "All",
      ...new Set(
        currentList
          .map((shop) => shop.Category)
          .filter(Boolean)
      ),
    ];
  }, [currentList]);

  /* --------------------------------
     Like Shop
  -------------------------------- */
  const handleLike = async (shop) => {
    if (!user) {
      alert("Please login to like a shop ❤️");
      return;
    }

    const uid = user.uid;
    const likedUsers = shop.LikedUsers || [];

    if (likedUsers.includes(uid)) {
      alert("You already liked this shop ❤️");
      return;
    }

    /* Likes currently supported only
       for owner shops */
    if (shop.source !== "owner") {
      return;
    }

    try {
      const ref = doc(
        db,
        "shops",
        shop.rowId
      );

      await updateDoc(ref, {
        Likes: increment(1),
        LikedUsers: arrayUnion(uid),
      });

      setOwnerShops((prev) =>
        prev.map((s) =>
          s.rowId === shop.rowId
            ? {
                ...s,
                Likes:
                  (Number(s.Likes) || 0) + 1,
                LikedUsers: [
                  ...(s.LikedUsers || []),
                  uid,
                ],
              }
            : s
        )
      );

    } catch (err) {
      console.error(
        "Like failed:",
        err
      );

      alert("Failed to like shop");
    }
  };

  /* --------------------------------
     Shuffle helper
  -------------------------------- */
  const shuffle = (array) => {
    const arr = [...array];

    for (
      let i = arr.length - 1;
      i > 0;
      i--
    ) {
      const j = Math.floor(
        Math.random() * (i + 1)
      );

      [arr[i], arr[j]] = [
        arr[j],
        arr[i],
      ];
    }

    return arr;
  };

  /* --------------------------------
     Filter + Sort
     
     IMPORTANT:
     Because currentList is memoized,
     this calculation does NOT rerun
     when only expandedShop changes.
  -------------------------------- */
  const filteredShops = useMemo(() => {
    const filtered = currentList.filter(
      (shop) => {
        const text =
          `${shop["Shop Name"] || ""} ${
            shop.Location || ""
          }`.toLowerCase();

        return (
          (!search ||
            text.includes(
              search.toLowerCase()
            )) &&
          (category === "All" ||
            shop.Category === category)
        );
      }
    );

    /* -----------------------
       Premium
    ----------------------- */
    const premium = filtered.filter(
      (shop) => isPremiumShop(shop)
    );

    /* -----------------------
       Normal
    ----------------------- */
    const normal = filtered.filter(
      (shop) => !isPremiumShop(shop)
    );

    /*
      Premium first.

      This shuffle happens only when:
      - shops change
      - tab changes
      - search changes
      - category changes

      It will NOT happen when clicking
      View More / View Less.
    */
    return [
      ...shuffle(premium),
      ...shuffle(normal),
    ];
  }, [
    currentList,
    search,
    category,
  ]);

  /* --------------------------------
     Render
  -------------------------------- */
  return (
    <div className="shops-wrap">
      <div className="shops-container">

        {/* =====================================
            HEADER
        ====================================== */}
        <div className="shops-header">

          <h1 className="shops-title">
            Shops
          </h1>

          <button
            type="button"
            className="add-shop-btn"
            onClick={() => {
              if (!user) {
                alert(
                  "Please login to add your shop"
                );

                return;
              }

              navigate("/user/shop");
            }}
          >
            Add my shop
          </button>

        </div>

        {/* =====================================
            OPTIONAL TABS
            Currently disabled
        ====================================== */}

        {/*
        <div className="shops-tab-bar">

          <button
            type="button"
            className={`shops-tab ${
              activeTab === "all"
                ? "active"
                : ""
            }`}
            onClick={() =>
              setActiveTab("all")
            }
          >
            <span className="shops-tab-icon">
              🌐
            </span>

            <span>
              All Shops
            </span>
          </button>

          <button
            type="button"
            className={`shops-tab ${
              activeTab === "owner"
                ? "active"
                : ""
            }`}
            onClick={() =>
              setActiveTab("owner")
            }
          >
            <span className="shops-tab-icon">
              🏪
            </span>

            <span>
              Posted by Owner
            </span>
          </button>

          <button
            type="button"
            className={`shops-tab ${
              activeTab === "volunteer"
                ? "active"
                : ""
            }`}
            onClick={() =>
              setActiveTab("volunteer")
            }
          >
            <span className="shops-tab-icon">
              🤝
            </span>

            <span>
              Posted by Volunteer
            </span>
          </button>

        </div>
        */}

        {/* =====================================
            MARQUEE
        ====================================== */}
        <div className="shop-marquee">

          <div className="shop-marquee-track">

            <span>
              🚀 Promote your shop • ❤️ Get direct call from customers • 💎 1 year free premium access available • 📞 Premium shops get call button • ⭐ Explore. Shop. Connect. ❤️ வாடிக்கையாளர்களிடமிருந்து நேரடி அழைப்புகளைப் பெறுங்கள் • 💎 1 வருட இலவச பிரீமியம் சேவையை பெறுங்கள் • 📞 பிரீமியம் கடைகளுக்கு நேரடி அழைப்பு சேவை கிடைக்கும் • ⭐ Explore. Shop. Connect.
            </span>

            <span>
              🚀 Promote your shop • ❤️ Get direct call from customers • 💎 1 year free premium access available • 📞 Premium shops get call button • ⭐ Explore. Shop. Connect. ❤️ வாடிக்கையாளர்களிடமிருந்து நேரடி அழைப்புகளைப் பெறுங்கள் • 💎 1 வருட இலவச பிரீமியம் சேவையை பெறுங்கள் • 📞 பிரீமியம் கடைகளுக்கு நேரடி அழைப்பு சேவை கிடைக்கும் • ⭐ Explore. Shop. Connect.
            </span>

          </div>

        </div>

        {/* =====================================
            FILTERS
        ====================================== */}
        <div className="shop-filters">

          <input
            className="shop-search"
            placeholder="Search shop name"
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />

          <select
            className="shop-select"
            value={category}
            onChange={(e) =>
              setCategory(e.target.value)
            }
          >
            {categories.map((cat) => (
              <option
                key={cat}
                value={cat}
              >
                {cat}
              </option>
            ))}
          </select>

        </div>

        {/* =====================================
            LOADING
        ====================================== */}
        {loading && (
          <p className="shops-sub">
            Loading shops...
          </p>
        )}

        {/* =====================================
            SHOP CARDS
        ====================================== */}
        {!loading &&
          filteredShops.map((shop) => {

            /* --------------------------------
               Shop Type
            -------------------------------- */
            const shopType =
              getShopType(shop);

            const isPremium =
              isPremiumShop(shop);

            /* --------------------------------
               Mobile
            -------------------------------- */
            const mobile =
              getMobileNumber(shop);

            /* --------------------------------
               Stable shop key
            -------------------------------- */
            const shopKey =
              `${shop.source}-${shop.rowId}`;

            /* --------------------------------
               Created date
            -------------------------------- */
            let createdDate = "";

            if (shop.createdAt) {
              try {
                if (
                  typeof shop.createdAt.toDate ===
                  "function"
                ) {
                  createdDate =
                    shop.createdAt
                      .toDate()
                      .toLocaleDateString();
                }
              } catch (err) {
                console.warn(
                  "Could not format createdAt:",
                  err
                );
              }
            }

            return (
              <div
                key={shopKey}
                className={`shop-card ${
                  isPremium
                    ? "shop-premium"
                    : "shop-normal"
                }`}
              >

                <div className="shop-info">

                  {/* =================================
                      IMAGE
                  ================================= */}
                  <div className="shop-image-box">

                    {shop["Shop Image"] ? (

                      <img
                        src={
                          shop["Shop Image"]
                        }
                        alt={
                          shop["Shop Name"] ||
                          "Shop"
                        }
                        className="shop-image"
                      />

                    ) : (

                      <div className="shop-image-placeholder">
                        🏪
                      </div>

                    )}

                  </div>

                  {/* =================================
                      CONTENT
                  ================================= */}
                  <div className="shop-content">

                    {/* SHOP NAME */}
                    <h3
                      className="shop-title"
                      onClick={() =>
                        navigate(
                          `/shop/${shop.rowId}`
                        )
                      }
                    >
                      {shop["Shop Name"]}
                    </h3>

                    {/* =================================
                        SHOP TYPE
                    ================================= */}
                    {activeTab === "volunteer" ? (

                      <span className="shop-type pending">
                        Pending Review
                      </span>

                    ) : (

                      <span
                        className={`shop-type ${
                          isPremium
                            ? "premium"
                            : "normal"
                        }`}
                      >
                        {isPremium
                          ? "Premium"
                          : "Normal"}
                      </span>

                    )}

                    {/* =================================
                        LOCATION + CATEGORY
                    ================================= */}
                    <p className="shop-meta">
                      {shop.Location}
                      {" • "}
                      {shop.Category}
                    </p>

                    {/* =================================
                        ACTIONS
                    ================================= */}
                    <div className="shop-actions">

                      {/* =================================
                          CALL BUTTON

                          Logged in:
                            Premium + phone = Call
                            Normal = hidden
                            Premium without phone = hidden

                          Logged out:
                            Login to Call
                      ================================= */}

                      {user ? (

                        isPremium &&
                        mobile ? (

                          <a
                            href={`tel:${mobile}`}
                            className="call-btn"
                          >
                            <FiPhoneCall
                              size={16}
                            />

                            <span>
                              Call
                            </span>
                          </a>

                        ) : null

                      ) : (

                        <button
                          type="button"
                          className="call-btn disabled"
                          onClick={() => {
                            alert(
                              "Please login to call this shop."
                            );

                            navigate(
                              "/login"
                            );
                          }}
                        >
                          <FiPhoneCall
                            size={16}
                          />

                          <span>
                            Login to Call
                          </span>
                        </button>

                      )}

                      {/* =================================
                          LIKE BUTTON

                          Currently disabled
                      ================================= */}

                      {/*
                      <button
                        type="button"
                        className="like-btn"
                        onClick={() =>
                          handleLike(shop)
                        }
                      >
                        <FiHeart
                          size={16}
                          fill={
                            shop.LikedUsers?.includes(
                              user?.uid
                            )
                              ? "currentColor"
                              : "none"
                          }
                        />

                        {shop.Likes > 0 && (
                          <span>
                            {shop.Likes}
                          </span>
                        )}
                      </button>
                      */}

                    </div>

                    {/* =================================
                        SOCIAL LINKS
                    ================================= */}
                    <div className="shop-socials">

                      {shop.Instagram && (
                        <button
                          type="button"
                          onClick={() =>
                            openSocial(
                              shop.Instagram
                            )
                          }
                        >
                          <FiInstagram />
                        </button>
                      )}

                      {shop.Facebook && (
                        <button
                          type="button"
                          onClick={() =>
                            openSocial(
                              shop.Facebook
                            )
                          }
                        >
                          <FiFacebook />
                        </button>
                      )}

                      {shop.X && (
                        <button
                          type="button"
                          onClick={() =>
                            openSocial(
                              shop.X
                            )
                          }
                        >
                          <FiTwitter />
                        </button>
                      )}

                      {shop.Threads && (
                        <button
                          type="button"
                          onClick={() =>
                            openSocial(
                              shop.Threads
                            )
                          }
                        >
                          <FiAtSign />
                        </button>
                      )}

                      {shop.Youtube && (
                        <button
                          type="button"
                          onClick={() =>
                            openSocial(
                              shop.Youtube
                            )
                          }
                        >
                          <FiYoutube />
                        </button>
                      )}

                    </div>

                    {/* =================================
                        VIEW MORE
                    ================================= */}
                    <button
                      type="button"
                      className="view-more-btn"
                      onClick={(e) => {
                        /*
                          IMPORTANT:
                          Prevent any parent form from
                          submitting/reloading.
                        */
                        e.preventDefault();

                        setExpandedShop(
                          (current) =>
                            current === shopKey
                              ? null
                              : shopKey
                        );
                      }}
                    >
                      {expandedShop === shopKey
                        ? "▲ View Less"
                        : "▼ View More"}
                    </button>

                    {/* =================================
                        EXTRA DETAILS
                    ================================= */}
                    {expandedShop === shopKey && (

                      <div className="shop-extra">

                        {/* DESCRIPTION */}
                        {shop.Description && (
                          <>
                            <h4>
                              About
                            </h4>

                            <p>
                              {
                                shop.Description
                              }
                            </p>
                          </>
                        )}

                        {/* ADDRESS */}
                        {shop.Address && (
                          <>
                            <h4>
                              Address
                            </h4>

                            <p>
                              {shop.Address}
                            </p>
                          </>
                        )}

                        {/* OPENING HOURS */}
                        {(shop["Opening Time"] ||
                          shop["Closing Time"]) && (

                          <>
                            <h4>
                              Opening Hours
                            </h4>

                            {shop[
                              "Opening Time"
                            ] && (
                              <p>
                                <strong>
                                  Opens:
                                </strong>{" "}
                                {
                                  shop[
                                    "Opening Time"
                                  ]
                                }
                              </p>
                            )}

                            {shop[
                              "Closing Time"
                            ] && (
                              <p>
                                <strong>
                                  Closes:
                                </strong>{" "}
                                {
                                  shop[
                                    "Closing Time"
                                  ]
                                }
                              </p>
                            )}

                          </>
                        )}

                        {/* SHOP FRONT PHOTO */}
                        {shop.ShopFrontPhoto && (
                          <>
                            <h4>
                              Shop Front
                            </h4>

                            <img
                              src={
                                shop.ShopFrontPhoto
                              }
                              alt="Shop Front"
                              className="shop-extra-image"
                            />
                          </>
                        )}

                        {/* VOLUNTEER SELFIE */}
                        {shop.VolunteerSelfie && (
                          <>
                            <h4>
                              Verified By Volunteer
                            </h4>

                            <img
                              src={
                                shop.VolunteerSelfie
                              }
                              alt="Volunteer"
                              className="shop-extra-image"
                            />
                          </>
                        )}

                        {/* SUBMITTED BY */}
                        {shop.volunteerName && (
                          <p>
                            <strong>
                              Submitted By:
                            </strong>{" "}
                            {
                              shop.volunteerName
                            }
                          </p>
                        )}

                        {/* STATUS */}
                        {shop.status && (
                          <p>
                            <strong>
                              Status:
                            </strong>{" "}
                            {String(
                              shop.status
                            ).replaceAll(
                              "_",
                              " "
                            )}
                          </p>
                        )}

                        {/* CREATED DATE */}
                        {createdDate && (
                          <p>
                            <strong>
                              Added On:
                            </strong>{" "}
                            {createdDate}
                          </p>
                        )}

                      </div>

                    )}

                  </div>
                </div>
              </div>
            );
          })}

        {/* =====================================
            NO RESULTS
        ====================================== */}
        {!loading &&
          filteredShops.length === 0 && (
            <p className="shops-sub">
              No shops found.
            </p>
          )}

      </div>
    </div>
  );
}